import store from "~/store";
import QueryBuilder from "./queryBuilder";
import { ServiceDetails, ServiceDetailsTable } from "~/types";
import { SQLiteInstance } from "../syncDataTypes";

export class ServiceOfflineService {
  private readonly SQLite: SQLiteInstance | null;
  private readonly TABLE_NAME = 'Service';
  private readonly qb: QueryBuilder;

  constructor() {
    this.SQLite = store.getState().app.offline.SQLiteConnection || null;
    this.qb = new QueryBuilder();
  }

  async reset(services: ServiceDetails[]): Promise<void> {
    if (!this.SQLite) return;

    const cappedServices = services.slice(0, 100);
    const { services: servicesToAdd, deleteServices } = await this.checkServices(cappedServices);

    if (deleteServices.length === 0 && servicesToAdd.length === 0) return;

    return new Promise<void>((resolve, reject) => {
      this.SQLite!.transaction(
        (tx) => {
          if (deleteServices.length) {
            tx.executeSql(this.qb.deleteIn(this.TABLE_NAME, "id", deleteServices));
          }
          if (servicesToAdd.length) {
            tx.executeSql(this.qb.insert(this.TABLE_NAME, servicesToAdd));
          }
        },
        reject,
        resolve
      );
    });
  }

  async checkServices(serviceDetails: ServiceDetails[]): Promise<{ services: ServiceDetailsTable[]; deleteServices: string[] }> {
    if (!this.SQLite) return { services: [], deleteServices: [] };

    const sql = this.qb.select(this.TABLE_NAME);

    return new Promise((resolve, reject) => {
      this.SQLite!.executeSql(
        sql,
        [],
        (resultSet) => {
          const newServices = serviceDetails.map(data => ({
            id: data.id,
            machineId: data.machineId,
            data: JSON.stringify(data),
          }));

          const dbServices: ServiceDetailsTable[] = this.qb.getAllResult(resultSet);

          const dbServicesMap = new Map(dbServices.map(s => [s.id, s]));
          const newServicesMap = new Map(newServices.map(s => [s.id, s]));

          const deleteServices = dbServices.filter(dbS => !newServicesMap.has(dbS.id)).map(s => s.id);

          const servicesToAdd = newServices.filter(newS => {
            const dbS = dbServicesMap.get(newS.id);
            return !dbS || dbS.data !== newS.data;
          });

          resolve({ services: servicesToAdd, deleteServices });
        },
        reject
      );
    });
  }

  async getService(id: string): Promise<ServiceDetails | undefined> {
    if (!this.SQLite) return undefined;

    const where = [{ name: 'id', value: id }];

    return new Promise((resolve, reject) => {
      this.SQLite!.executeSql(
        this.qb.select(this.TABLE_NAME, [], where),
        [],
        (result) => {
          const service = this.qb.getSingleResult(result);
          if (!service) return reject(new Error(`Service with id ${id} not found`));

          const data: ServiceDetails = {
            ...JSON.parse(service.data),
            id: service.id
          };

          resolve(data);
        },
        reject
      );
    });
  }

  updateServiceDetail(service: ServiceDetails): void {
    if (!this.SQLite) return;

    if (store.getState().machines.inFavorite(service.machineId)) return;

    this.SQLite.transaction((tx) => {
      tx.executeSql(
        this.qb.update(
          this.TABLE_NAME,
          {
            id: service.id,
            machineId: service.machineId,
            data: JSON.stringify(service)
          },
          [{ name: 'id', value: service.id }]
        )
      );
    });
  }
}